import { FC, useState } from 'react';
import { Box, Typography, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import type { FlightItem } from '../../../../../types/flight.types';
import { formatDateWithYear } from '../../../../../utils/dateUtils';
import { getCurrencySymbol } from '../../../../../constants/currency';
import { MarkerPinIcon, StopsIcon } from '../../../../../components/Icons';
import { useIsMobile } from '../../../../../hooks/useWindowWidth';
import CustomButton from '../../../../../common/CustomButton';
import ConfirmActionModal from '../../../../../common/ConfirmActionModal/ConfirmActionModal';

import styles from './FlightResultCard.module.scss';

function formatTime(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function formatDateTime(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${formatDateWithYear(d)} ${d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })}`;
}

function stopsLabel(n: number): string {
  if (n === 0) return '0 stops';
  if (n === 1) return '1 stop';
  return `${n} stops`;
}

export interface FlightResultCardProps {
  flight: FlightItem;
  isRoundTrip: boolean;
  onSave: (flight: FlightItem) => void;
  showActions?: boolean;
  showShopButton?: boolean;
  isSaving?: boolean;
}

const FlightResultCard: FC<FlightResultCardProps> = ({
  flight,
  isRoundTrip,
  onSave,
  showActions = true,
  showShopButton = true,
  isSaving = false,
}) => {
  const isMobile = useIsMobile();
  const [expandedOutbound, setExpandedOutbound] = useState(false);
  const [expandedReturn, setExpandedReturn] = useState(false);
  const [shopConfirmOpen, setShopConfirmOpen] = useState(false);
  const shopLink = flight.link?.trim() ?? '';
  const canShop = showShopButton && Boolean(shopLink);

  const hasReturnLeg = Boolean(isRoundTrip && flight.return_departure_date_time && flight.return_arrival_date_time);

  const outboundTimes = `${formatTime(flight.depart_departure_date_time)} – ${formatTime(flight.depart_arrival_date_time)}`;
  const outboundRoute = `${flight.depart_departure_airport} - ${flight.depart_arrival_airport}`;
  const outboundAirlineLine = flight.depart_airline_name || flight.depart_airline;

  const returnTimes =
    hasReturnLeg && flight.return_departure_date_time && flight.return_arrival_date_time
      ? `${formatTime(flight.return_departure_date_time)} – ${formatTime(flight.return_arrival_date_time)}`
      : null;
  const returnRoute =
    hasReturnLeg && flight.return_departure_airport && flight.return_arrival_airport
      ? `${flight.return_departure_airport} - ${flight.return_arrival_airport}`
      : '';
  const returnAirlineLine =
    hasReturnLeg && (flight.return_airline_name || flight.return_airline)
      ? flight.return_airline_name || flight.return_airline || ''
      : '';

  const showSecondSummaryRow = Boolean(hasReturnLeg && returnTimes);

  const bothDetailSectionsOpen = showSecondSummaryRow && expandedOutbound && expandedReturn;

  const renderOutboundDetails = () => (
    <Box className={styles.details}>
      <Typography
        variant="h9Bold"
        className={styles.detailsHeading}
      >
        Details
      </Typography>
      <Typography
        variant="h10"
        className={styles.detailLine}
      >
        From{' '}
        <span className={styles.detailLineBold}>
          {flight.depart_departure_airport_name} ({flight.depart_departure_airport})
        </span>
      </Typography>
      <Typography
        variant="h10"
        className={styles.detailLine}
      >
        To{' '}
        <span className={styles.detailLineBold}>
          {flight.depart_arrival_airport_name} ({flight.depart_arrival_airport})
        </span>
      </Typography>
      <Box className={styles.detailsDivider} />
      <Box className={styles.depArrGrid}>
        <Box className={styles.depArrGridItem}>
          <Typography
            variant="h11"
            className={styles.detailMuted}
          >
            Departure
          </Typography>
          <Typography variant="h11Semibold" color="#222B3A">{formatDateTime(flight.depart_departure_date_time)}</Typography>
        </Box>
        <Box className={styles.depArrGridItem}>
          <Typography
            variant="h11"
            className={styles.detailMuted}
          >
            Arrival
          </Typography>
          <Typography variant="h11Semibold" color="#222B3A">{formatDateTime(flight.depart_arrival_date_time)}</Typography>
        </Box>
      </Box>
      <Typography
        variant="h10"
        className={styles.detailLine}
      >
        Airline{' '}
        <span className={styles.detailLineBold}>
          {flight.depart_airline} {flight.depart_airline_name}
        </span>
      </Typography>
      <Typography
        variant="h10"
        className={styles.detailLine}
      >
        Flight # <span className={styles.detailLineBold}>{flight.depart_flight_number}</span>
      </Typography>
      {flight.depart_stops_airports ? (
        <Typography
          variant="h10"
          className={styles.detailLine}
        >
          Stops: <span className={styles.detailLineBold}>{flight.depart_stops_airports}</span>
        </Typography>
      ) : null}
    </Box>
  );

  const renderReturnDetails = () => {
    if (!hasReturnLeg || !flight.return_departure_date_time) return null;
    return (
      <Box className={styles.details}>
        <Typography
          variant="h9Bold"
          className={styles.detailsHeading}
        >
          Details
        </Typography>
        <Typography
          variant="h10"
          className={styles.detailLine}
        >
          From{' '}
          <span className={styles.detailLineBold}>
            {flight.return_departure_airport_name || flight.return_departure_airport} ({flight.return_departure_airport}
            )
          </span>
        </Typography>
        <Typography
          variant="h10"
          className={styles.detailLine}
        >
          To{' '}
          <span className={styles.detailLineBold}>
            {flight.return_arrival_airport_name || flight.return_arrival_airport} ({flight.return_arrival_airport})
          </span>
        </Typography>
        <Box className={styles.detailsDivider} />
        <Box className={styles.depArrGrid}>
          <Box className={styles.depArrGridItem}>
            <Typography
              variant="h10"
              className={styles.detailMuted}
            >
              Departure
            </Typography>
            <Typography variant="h10Semibold">{formatDateTime(flight.return_departure_date_time)}</Typography>
          </Box>
          <Box className={styles.depArrGridItem}>
            <Typography
              variant="h10"
              className={styles.detailMuted}
            >
              Arrival
            </Typography>
            <Typography variant="h10Semibold">{formatDateTime(flight.return_arrival_date_time || '')}</Typography>
          </Box>
        </Box>
        <Typography
          variant="h10"
          className={styles.detailLine}
        >
          Airline{' '}
          <span className={styles.detailLineBold}>
            {flight.return_airline} {flight.return_airline_name}
          </span>
        </Typography>
        <Typography
          variant="h10"
          className={styles.detailLine}
        >
          Flight # <span className={styles.detailLineBold}>{flight.return_flight_number}</span>
        </Typography>
        {flight.return_stops_airports ? (
          <Typography
            variant="h10"
            className={styles.detailLine}
          >
            Stops: <span className={styles.detailLineBold}>{flight.return_stops_airports}</span>
          </Typography>
        ) : null}
      </Box>
    );
  };

  const renderLegMeta = (duration: string | undefined, stopCount: number) => (
    <Box className={styles.legMeta}>
      <Box className={styles.duration}>
        <Typography variant="h11Semibold" color="#222B3A">{duration || '—'}</Typography>
      </Box>
      <Box className={styles.stops}>
        {stopCount === 0 && isMobile ? <StopsIcon size={18} /> : <MarkerPinIcon size={18} />}
        <Typography
          variant="h11"
          className={styles.stopsText}
        >
          {stopsLabel(stopCount)}
        </Typography>
      </Box>
    </Box>
  );

  const renderToggle = (expanded: boolean, onToggle: () => void, id: string) => (
    <Box
      className={styles.toggleRow}
      onClick={e => {
        e.stopPropagation();
        onToggle();
      }}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      aria-controls={id}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
    >
      <Typography variant="h11">{expanded ? 'Show less' : 'Show more'}</Typography>
      <ExpandMoreIcon
        sx={{
          fontSize: 20,
          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      />
    </Box>
  );

  const handleShopContinue = () => {
    if (!shopLink) return;
    setShopConfirmOpen(false);
    window.open(shopLink, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box className={styles.card}>
      <Box className={styles.cardBody}>
        <Box className={`${styles.legSummaryRow}${showSecondSummaryRow ? ` ${styles.hasReturnAfter}` : ''}`}>
          <Box className={`${styles.legMain}${showSecondSummaryRow ? ` ${styles.firstSummaryRowSpacing}` : ''}`}>
            <Typography
              variant="h9Semibold"
              className={styles.times}
            >
              {outboundTimes}
            </Typography>
            <Typography
              variant="h11"
              className={styles.routeLine}
            >
              {outboundRoute}, {outboundAirlineLine}
            </Typography>
            {renderToggle(expandedOutbound, () => setExpandedOutbound(v => !v), 'flight-out-details')}
          </Box>

          <Box className={`${styles.legMetaCell}${showSecondSummaryRow ? ` ${styles.firstSummaryRowSpacing}` : ''}`}>
            {renderLegMeta(flight.depart_duration, flight.depart_stop_number)}
          </Box>
        </Box>

        <Box className={`${styles.asideColumn}${showSecondSummaryRow ? ` ${styles.firstSummaryRowSpacing}` : ''}`}>
          <Box className={styles.priceBlock}>
            <Typography className={styles.priceLabel}>from</Typography>
            <Typography className={styles.priceValue}>
              {getCurrencySymbol(flight.currency)}
              {flight.price.toLocaleString('en-US')}
            </Typography>
          </Box>
          {showActions ? (
            <Box className={styles.actions}>
              {canShop ? (
                <CustomButton
                  variant="outlined"
                  btnType="button"
                  onClick={() => setShopConfirmOpen(true)}
                  customClass={styles.shopButton}
                >
                  Shop
                </CustomButton>
              ) : null}
              <CustomButton
                variant="contained"
                btnType="button"
                onClick={() => onSave(flight)}
                customClass={styles.saveButton}
                disabled={isSaving}
              >
                {isSaving ? 'Saving…' : 'Save'}
              </CustomButton>
            </Box>
          ) : null}
        </Box>

        <Box
          className={`${styles.gridFullSpan}${bothDetailSectionsOpen ? ` ${styles.betweenOpenDetailsSections}` : ''}`}
        >
          <Collapse
            in={expandedOutbound}
            timeout={280}
            unmountOnExit={false}
          >
            <Box
              id="flight-out-details"
              className={styles.detailsWrap}
            >
              {renderOutboundDetails()}
            </Box>
          </Collapse>
        </Box>

        {hasReturnLeg && returnTimes ? (
          <>
            <Box className={`${styles.legSummaryRow} ${styles.legSummaryRowReturn}`}>
              <Box className={`${styles.legMain}${expandedReturn ? ` ${styles.returnSummaryRowExpandedSpacing}` : ''}`}>
                <Typography
                  variant="h10Semibold"
                  className={styles.times}
                >
                  {returnTimes}
                </Typography>
                <Typography
                  variant="h11"
                  className={styles.routeLine}
                >
                  {returnRoute}, {returnAirlineLine}
                </Typography>
                {renderToggle(expandedReturn, () => setExpandedReturn(v => !v), 'flight-ret-details')}
              </Box>

              <Box
                className={`${styles.legMetaCell}${expandedReturn ? ` ${styles.returnSummaryRowExpandedSpacing}` : ''}`}
              >
                {renderLegMeta(flight.return_duration, flight.return_stop_number ?? 0)}
              </Box>
            </Box>

            <Box
              className={`${styles.asidePlaceholder}${
                expandedReturn ? ` ${styles.returnSummaryRowExpandedSpacing}` : ''
              }`}
              aria-hidden
            />

            <Box className={`${styles.gridFullSpan}${expandedReturn ? ` ${styles.returnDetailsOpenSpacing}` : ''}`}>
              <Collapse
                in={expandedReturn}
                timeout={280}
                unmountOnExit={false}
              >
                <Box
                  id="flight-ret-details"
                  className={styles.detailsWrap}
                >
                  {renderReturnDetails()}
                </Box>
              </Collapse>
            </Box>
          </>
        ) : null}
      </Box>
      <ConfirmActionModal
        open={shopConfirmOpen}
        onClose={() => setShopConfirmOpen(false)}
        onConfirm={handleShopContinue}
        title="External Website"
        description={
          <>
            You are being redirected to an external website to continue your flight research. Flight prices may vary
            and may not reflect the prices shown on <strong>TravGenius</strong>. Please confirm all prices and travel
            details directly on the external website.
          </>
        }
        confirmLabel="Continue"
        cancelLabel="Cancel"
      />
    </Box>
  );
};

export default FlightResultCard;
